/**
 * Shared filtering for Alerts / Issues UI so sidebar counts, badges, and lists
 * stay aligned when marketplace / seller-account / date filters apply.
 */

export function itemMatchesMarketFilter (item, filterByMarket, products) {
  if (!filterByMarket || filterByMarket.length === 0) return true
  var mp = (item.marketplace == null || item.marketplace === '') ? '' : String(item.marketplace).toUpperCase()
  if (!mp && products && item.productId && products[item.productId]) {
    var pmp = products[item.productId].marketplace
    if (pmp != null && pmp !== '') mp = String(pmp).toUpperCase()
  }
  if (mp === 'GB') mp = 'UK'
  var filters = filterByMarket
  for (var i = 0; i < filters.length; i++) {
    var f = String(filters[i]).toUpperCase()
    var x = f === 'GB' ? 'UK' : f
    if (x === mp) return true
  }
  return false
}

export function itemMatchesSellerFilter (item, filterBySellAcc) {
  if (!filterBySellAcc || filterBySellAcc.length === 0) return true
  if (item.sellerId === null || item.sellerId === undefined) return false
  var sid = item.sellerId
  var filters = filterBySellAcc
  for (var j = 0; j < filters.length; j++) {
    var f = filters[j]
    if (String(f) === String(sid)) return true
    var nf = Number(f)
    var ns = Number(sid)
    if (!isNaN(nf) && !isNaN(ns) && nf === ns) return true
  }
  return false
}

export function itemHasProductContext (i, products) {
  if (i.productId != null && i.productId !== '' && products[i.productId]) return true
  if (i.productId != null && i.productId !== '') {
    if (i.asin && String(i.asin).trim()) return true
    if (i.title && String(i.title).trim()) return true
    var msg = i.statusDetails && i.statusDetails.message
    if (msg && String(msg).trim()) return true
  }
  if (i.watchDogId != null && i.watchDogId !== '' && (i.productId == null || i.productId === '')) {
    return true
  }
  return false
}

export function itemMatchesDateRange (item, dateRange, nowMs) {
  if (!dateRange || dateRange === 'all' || dateRange === 'all time') return true
  var now = nowMs != null ? nowMs : Date.now()
  var cutoff = 0
  if (dateRange === 'today') {
    var todayStart = new Date(now)
    todayStart.setHours(0, 0, 0, 0)
    cutoff = todayStart.getTime()
  } else if (dateRange === '7d') {
    cutoff = now - 7 * 24 * 60 * 60 * 1000
  } else if (dateRange === '30d') {
    cutoff = now - 30 * 24 * 60 * 60 * 1000
  } else if (dateRange === '90d') {
    cutoff = now - 90 * 24 * 60 * 60 * 1000
  }
  if (cutoff <= 0) return true
  if (!item.date) return true
  return new Date(item.date).getTime() >= cutoff
}

/**
 * Items visible after marketplace / seller / product-context / date filters (matches IssuesCategoryDetail).
 */
export function filterIssuesItemsGlobally (itemsArray, products, filterByMarket, filterBySellAcc, dateRange, nowMs) {
  var arr = itemsArray.slice()
  if (filterByMarket && filterByMarket.length > 0) {
    arr = arr.filter(function (i) { return itemMatchesMarketFilter(i, filterByMarket, products) })
  }
  if (filterBySellAcc && filterBySellAcc.length > 0) {
    arr = arr.filter(function (i) { return itemMatchesSellerFilter(i, filterBySellAcc) })
  }
  arr = arr.filter(function (i) { return itemHasProductContext(i, products) })
  arr = arr.filter(function (i) { return itemMatchesDateRange(i, dateRange, nowMs) })
  return arr
}

/**
 * Competitor price email that did not undercut: watchdog stays GREEN (status 0).
 * The Issues page still lists it on Active until the user marks it resolved.
 */
export function isCompetitorPriceOpenChange (item) {
  if (!item || item.muted || item.issueStatus === 3) return false
  if (item.status !== 0 && item.status !== 99 && String(item.status).toLowerCase() !== 'green') return false
  var d = item.statusDetails || {}
  var change = d.last_competitor_change || d.lastCompetitorChange
  return !!(change && typeof change === 'object')
}

export function isIssuesActiveTabItem (item) {
  if (isCompetitorPriceOpenChange(item)) return true
  return (item.status === 1 || item.status === 2) && !item.muted && item.issueStatus < 3
}

export function isIssuesResolvedTabItem (item) {
  if (!item || item.muted) return false
  if (isCompetitorPriceOpenChange(item)) return false
  /** Cleared in UI via POST set-issue-status → issueStatus 3; API row may still carry severity in `status`. */
  if (item.issueStatus === 3) return true
  return item.status === 99 || item.status === 0
}

/** Sidebar category totals: open alerts only (warning/alert severity), matching the Active tab — not OK / cleared rows (status 0). */
export function countIssuesCategoryVisible (itemsObj, products, filterByMarket, filterBySellAcc, dateRange, nowMs) {
  var arr = Object.values(itemsObj || {})
  arr = filterIssuesItemsGlobally(arr, products || {}, filterByMarket || [], filterBySellAcc || [], dateRange, nowMs)
  var active = 0
  for (var i = 0; i < arr.length; i++) {
    if (isIssuesActiveTabItem(arr[i])) active++
  }
  return active
}

/** "New" badge: unread-style alerts (issueStatus === 0) among globally filtered items. */
export function countIssuesNewAlertsFiltered (itemsObj, products, filterByMarket, filterBySellAcc, dateRange, nowMs) {
  var arr = Object.values(itemsObj || {})
  arr = filterIssuesItemsGlobally(arr, products || {}, filterByMarket || [], filterBySellAcc || [], dateRange, nowMs)
  var count = 0
  for (var j = 0; j < arr.length; j++) {
    var item = arr[j]
    if (item && !item.muted && item.issueStatus === 0 &&
        ((item.status === 1 || item.status === 2) || isCompetitorPriceOpenChange(item))) {
      count++
    }
  }
  return count
}

/** Sort ordering helpers — keep parity with IssuesCategoryDetail.getFilteredItems tail sort. */
export function sortIssuesItemsForTab (arr, tab, products) {
  arr.sort(function (a, b) {
    var prodA = a.productId && products[a.productId] ? products[a.productId] : null
    var prodB = b.productId && products[b.productId] ? products[b.productId] : null
    var titleA = prodA && prodA.productTitle ? prodA.productTitle : ''
    var titleB = prodB && prodB.productTitle ? prodB.productTitle : ''
    return titleA < titleB ? -1 : (titleA > titleB ? 1 : 0)
  })
  if (tab === 'active') {
    arr.sort(function (a, b) { return b.status - a.status })
  }
  return arr
}



// WEBPACK FOOTER //
// ./src/utils/issuesAlertFilters.js